import { CommonModule } from "@angular/common";
import { Component, Input, OnInit, inject } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { VFIconComponent } from "../../../shared/component/vficon/vficon.component";
import {
  NotificationItem,
  NotificationSeverity,
  NotificationType,
  NotificationsService,
} from "../../../shared/services/notifications.service";

type BellView = "all" | "unread" | "high";

@Component({
  selector: "app-user-notification-dropdown",
  standalone: true,
  imports: [CommonModule, FormsModule, VFIconComponent],
  templateUrl: "./user-notification-dropdown.component.html",
  styleUrls: ["./user-notification-dropdown.component.scss"],
})
export class UserNotificationDropdownComponent implements OnInit {
  private readonly notificationsService = inject(NotificationsService);
  private readonly excludedStorageKey = "pcs.user.notifications.excluded";
  private isRefreshingFromApi = false;

  @Input() public currentTenant: string | null = null;
  @Input() public isLoggedIn = false;

  public activeBellView: BellView = "all";
  public bellSearchTerm = "";
  public bellFilterType: NotificationType | "all" = "all";
  public bellFilterSeverity: NotificationSeverity | "all" = "all";
  public bellFilterCloudProvider = "all";
  public excludedNotificationIds = new Set<string>();

  public async ngOnInit(): Promise<void> {
    this.excludedNotificationIds = this.loadExcludedNotificationIds();
    if (!this.isLoggedIn) {
      return;
    }
    await this.refreshUserNotifications();
  }

  public async refreshUserNotifications(): Promise<void> {
    if (!this.isLoggedIn) {
      return;
    }

    if (this.isRefreshingFromApi) {
      return;
    }

    this.isRefreshingFromApi = true;

    try {
      await this.notificationsService.refreshNotificationsFromUserApi({
        limit: 50,
        daysBack: 30,
        includeRead: true,
      });
    } finally {
      this.isRefreshingFromApi = false;
    }
  }

  public get bellNotifications(): NotificationItem[] {
    return this.notificationsService.getNotificationsForAudience(this.currentTenant);
  }

  public get bellUnreadCount(): number {
    return this.notificationsService.getUnreadCountForAudience(this.currentTenant);
  }

  public get bellHighCount(): number {
    return this.bellNotifications.filter((notification) => notification.priority === "high").length;
  }

  public get filteredBellNotifications(): NotificationItem[] {
    let notifications: NotificationItem[] = this.bellNotifications.filter((notification) => {
      if (this.activeBellView === "unread") {
        return notification.unread;
      }

      if (this.activeBellView === "high") {
        return notification.priority === "high";
      }

      return true;
    });

    if (this.bellSearchTerm.trim()) {
      const term: string = this.bellSearchTerm.toLowerCase();
      notifications = notifications.filter(
        (notification) =>
          notification.headline.toLowerCase().includes(term) || notification.description.toLowerCase().includes(term),
      );
    }

    if (this.bellFilterType !== "all") {
      notifications = notifications.filter((notification) => notification.type === this.bellFilterType);
    }

    if (this.bellFilterSeverity !== "all") {
      notifications = notifications.filter((notification) => notification.severity === this.bellFilterSeverity);
    }

    if (this.bellFilterCloudProvider !== "all") {
      notifications = notifications.filter((notification) =>
        notification.cloud_provider.includes(this.bellFilterCloudProvider),
      );
    }

    return notifications.slice(0, 10);
  }

  public setBellView(view: BellView): void {
    this.activeBellView = view;
  }

  public async markNotificationAsRead(notification: NotificationItem, $event?: Event): Promise<void> {
    if ($event) {
      $event.stopPropagation();
    }

    if (notification.unread) {
      await this.notificationsService.markAsRead(notification.id);
    }
  }

  public isExcluded(notificationId: string): boolean {
    return this.excludedNotificationIds.has(notificationId);
  }

  public async toggleNotificationExclude(notification: NotificationItem, $event: Event): Promise<void> {
    $event.preventDefault();
    $event.stopPropagation();

    const isCurrentlyExcluded: boolean = this.isExcluded(notification.id);
    const action: "unexclude" | "exclude" = isCurrentlyExcluded ? "unexclude" : "exclude";
    const wasSuccessful: boolean = await this.notificationsService.markNotification(notification.id, action);

    if (!wasSuccessful) {
      return;
    }

    if (isCurrentlyExcluded) {
      this.excludedNotificationIds.delete(notification.id);
    } else {
      this.excludedNotificationIds.add(notification.id);
    }

    this.persistExcludedNotificationIds();
  }

  public resetBellFilters(): void {
    this.bellSearchTerm = "";
    this.bellFilterType = "all";
    this.bellFilterSeverity = "all";
    this.bellFilterCloudProvider = "all";
  }

  public getAvailableFilterTypes(): NotificationType[] {
    return this.getUniqueValues(this.bellNotifications.map((notification) => notification.type));
  }

  public getAvailableFilterSeverities(): NotificationSeverity[] {
    return this.getUniqueValues(this.bellNotifications.map((notification) => notification.severity));
  }

  public getAvailableFilterCloudProviders(): string[] {
    return this.getUniqueValues(this.bellNotifications.flatMap((notification) => notification.cloud_provider));
  }

  public getNotificationTypeLabel(type: NotificationType): string {
    return this.notificationsService.getTypeLabel(type);
  }

  private loadExcludedNotificationIds(): Set<string> {
    const rawValue: string | null = localStorage.getItem(this.excludedStorageKey);
    if (!rawValue) {
      return new Set<string>();
    }

    try {
      const parsed: unknown = JSON.parse(rawValue);
      if (!Array.isArray(parsed)) {
        return new Set<string>();
      }

      return new Set<string>(parsed.filter((value): value is string => typeof value === "string"));
    } catch {
      return new Set<string>();
    }
  }

  private persistExcludedNotificationIds(): void {
    localStorage.setItem(this.excludedStorageKey, JSON.stringify(Array.from(this.excludedNotificationIds)));
  }

  private getUniqueValues<T extends string>(values: T[]): T[] {
    return Array.from(new Set(values)).sort();
  }
}
